import React, { useRef, useEffect, useState } from 'react';
import '../styles/OtpVerification.scss'; // Import CSS
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { otpInitialValues, otpValidationSchema } from '../../../Extras/AuthenticationFormik';
import { useDispatch, useSelector } from 'react-redux';
import Service from '../services/Service';
import showToast from '../../../../../Shared/Utils/ToastNotification';
import { userlogin } from '../../../../../Shared/Slice/AuthSlice';
import img1 from '../../../../../assets/imgs/0a8f62242ec799f152866e747fa102562420968c.png';
import img2 from '../../../../../assets/imgs/0ce5c8b58d533e324615a59d7f991dd3977bd4b3.jpg';
import img3 from '../../../../../assets/imgs/1fe0554d501cb3c912ebad3f44b26392392df6a0.png';
import img4 from '../../../../../assets/imgs/09bdaab1a2034e127e45ce747d211455d4d06b6a.jpg';
import img5 from '../../../../../assets/imgs/8858e91672837f1ec1cf70fb12151be8cfd7f945.jpg';
import brandlogo from '../../../../../assets/imgs/swimscore-logo.png';
import Swal from 'sweetalert2';

const OtpVerification = () => {
  const loginImages = [img1, img2, img3, img4, img5];

  // Randomly select initial image
  const [currentImageIndex, setCurrentImageIndex] = useState(() => Math.floor(Math.random() * loginImages.length));
  const navigate = useNavigate();
  const inputRefs = useRef([]);
  const dispatch = useDispatch();
  const location = useLocation();
  const { login } = location.state || {}; // safely extract
  const email = useSelector((state) => state.auth?.pendingEmail?.email);
  const justRegistered = useSelector((state) => state.auth?.pendingEmail?.justRegistered);
  let role = useSelector((state) => state.auth.role);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Rotate images every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % loginImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [loginImages.length]);

  useEffect(() => {
    if (!email) navigate('/sign-up'); // redirect if no email
  }, [email, navigate]);

  // Resend cooldown timer
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setInterval(() => setResendCooldown((c) => c - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);
  useEffect(() => {
    if (email) {
      setResendCooldown(30); // start 30 sec timer when page loads with email
    }
  }, [email]);

  const formik = useFormik({
    initialValues: otpInitialValues,
    validationSchema: otpValidationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    // eslint-disable-next-line no-unused-vars
    onSubmit: async (values, { setSubmitting, setFieldError, setFieldTouched }) => {
      setFieldTouched('otp', true);

      setFieldError('otp', undefined);

      const isComplete = values.otp.every((digit) => digit && digit.length === 1 && /^[0-9]$/.test(digit));
      if (!isComplete) {
        setFieldError('otp', 'Please enter valid OTP');
        setSubmitting(false);
        return;
      }
      try {
        const otpValue = values.otp.join('');
        const res = login ? await Service.verifyOtpLogin({ email, otp: otpValue }) : await Service.verifyOtp({ email, otp: otpValue });

        if (res?.status == 200) {
          setFieldError('otp', undefined);
          showToast('success', res.data.message);
          // Redirect after success
          if (res?.data?.redirect_url) {
            Swal.fire({
              title: 'Complete Your Intake Form',
              text: 'You need to fill out your intake form before continuing.',
              icon: 'info',
              confirmButtonText: 'Go to Intake Form',
              allowOutsideClick: false,
            }).then(() => {
              window.location.replace(res.data.redirect_url);
            });
            return;
          }
          if (res?.data?.refresh) {
            sessionStorage.setItem('refresh_token', res.data.refresh);
          }
          dispatch(
            userlogin({
              token: res.data.access,
              user: res.data.user || { email },
              justRegistered: !!justRegistered,
            })
          );
          showToast('success', 'OTP verified!');
          if (role?.toLowerCase() === 'admin') navigate('/admin/dashboard');
          else if (justRegistered) navigate('/subscriptions');
          else navigate('/dashboard');
        } else if (res?.data?.access) {
          // (keep this for backward compatibility)
          setFieldError('otp', undefined);
          if (res?.data?.refresh) {
            sessionStorage.setItem('refresh_token', res.data.refresh);
          }
          dispatch(
            userlogin({
              token: res.data.access,
              user: res.data.user || { email },
              justRegistered: !!justRegistered,
            })
          );
          showToast('success', 'OTP verified!');
          if (role?.toLowerCase() === 'admin') navigate('/admin/dashboard');
          else if (justRegistered) navigate('/subscriptions');
          else navigate('/dashboard');
        } else {
          const errorMessage = res?.data?.detail || res?.data?.message || 'OTP verification failed';
          setFieldError('otp', errorMessage);
          showToast('error', errorMessage);
          return;
        }
      } catch (err) {
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'OTP verification failed';
        setFieldError('otp', errorMessage);
        showToast('error', errorMessage);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleChange = (e, index) => {
    const { value } = e.target;
    if (value.length > 1) {
      const numericData = value.replace(/\D/g, '');
      if (numericData.length > 0) {
        const digits = numericData.slice(0, 6).split('');
        const newOtp = ['', '', '', '', '', ''];
        digits.forEach((digit, idx) => {
          if (idx < 6) {
            newOtp[idx] = digit;
          }
        });
        formik.setFieldError('otp', undefined);
        formik.setFieldValue('otp', newOtp, false);
        const lastIndex = Math.min(digits.length - 1, 5);
        setTimeout(() => {
          if (inputRefs.current[lastIndex]) {
            inputRefs.current[lastIndex].focus();
          }
        }, 10);
        return;
      }
    }

    if (!/^[0-9]?$/.test(value)) return;

    if (formik.errors.otp) {
      formik.setFieldError('otp', undefined);
    }

    const newOtp = [...formik.values.otp];
    newOtp[index] = value;
    formik.setFieldValue('otp', newOtp);

    // auto-focus next
    if (value && index < inputRefs.current.length - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !formik.values.otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const pastedData =
      e.clipboardData?.getData('text') || e.nativeEvent?.clipboardData?.getData('text') || window.clipboardData?.getData('text') || '';
    if (!pastedData) return;
    const numericData = pastedData.replace(/\D/g, '');
    if (numericData.length === 0) return;
    const digits = numericData.slice(0, 6).split('');
    const newOtp = ['', '', '', '', '', ''];
    digits.forEach((digit, idx) => {
      if (idx < 6) {
        newOtp[idx] = digit;
      }
    });
    formik.setFieldError('otp', undefined);
    formik.setFieldValue('otp', newOtp, false);
    setTimeout(() => {
      if (digits.length === 6) {
        if (inputRefs.current[5]) {
          inputRefs.current[5].blur();
        }
      } else if (digits.length > 0) {
        const nextIndex = Math.min(digits.length, 5);
        if (inputRefs.current[nextIndex]) {
          inputRefs.current[nextIndex].focus();
        }
      }
    }, 50);
  };

  const handleResend = async (e) => {
    e.preventDefault();
    if (resendCooldown > 0) return;
    try {
      await Service.otpSendonMail({ email });
      showToast('success', 'OTP resent successfully!');
      setResendCooldown(30); // 30 seconds cooldown
    } catch (err) {
      showToast('error', err.response?.data?.message || 'Failed to resend OTP');
    }
  };

  // helper to show an error message under fields
  const error = (name) => {
    if (!formik.touched[name] || !formik.errors[name]) return null;
    const isComplete = formik.values.otp.every((digit) => digit && digit.length === 1 && /^[0-9]$/.test(digit));
    if (isComplete && formik.errors[name] === 'Please enter valid OTP') {
      return null;
    }
    return <div className="error">{formik.errors[name]}</div>;
  };

  return (
    <>
      <div className="login">
        <div className="login-content">
          <img className="login-video" src={loginImages[currentImageIndex]} alt="Login background" />
          <div className="logo">
            <img src={brandlogo} alt="" />
          </div>
        </div>
        <div className="login-form loginpage">
          <div className="login-head-cont">
            <h2>Enter Code</h2>
            <p>
              Verification Code Sent on <strong>{email}</strong>
            </p>
          </div>
          <form onSubmit={formik.handleSubmit}>
            <div className="form-group">
              <div className="otp-inputs" onPaste={handlePaste} onPasteCapture={handlePaste}>
                {formik.values.otp.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    name={`otp[${index}]`}
                    value={digit}
                    ref={(el) => (inputRefs.current[index] = el)}
                    onChange={(e) => handleChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={handlePaste}
                    onPasteCapture={handlePaste}
                    className={`otp-input form-control ${formik.touched.otp && formik.errors.otp ? 'is-invalid' : ''}`}
                    aria-label={`Digit ${index + 1} of OTP`}
                  />
                ))}
              </div>
              {error('otp')}
            </div>
            <button type="submit" disabled={formik.isSubmitting || !formik.values.otp.every((d) => d !== '')} className="verify-btn btn">
              {formik.isSubmitting ? 'Verifying...' : 'Verify'}
              {/* Verify */}
            </button>
          </form>

          <div className="login-foot">
            <div className="login-link">
              Did not receive code?{' '}
              <Link to="" onClick={handleResend}>
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
                {/* Resend Code */}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OtpVerification;
